/**
 * Template Parameter System
 *
 * 템플릿 프롬프트에서 ${KEY:-DEFAULT_VALUE} 패턴을 파싱하고 치환하는 유틸리티.
 * 사용자가 핵심 값만 쉽게 수정할 수 있도록 UI를 제공한다.
 */

import { tDynamic } from '@/i18n'

/** 패턴: ${KEY:-DEFAULT_VALUE} (영문 대문자 또는 한글 키 허용) */
const PARAM_REGEX = /\$\{([A-Z_가-힣][A-Z0-9_가-힣]*):-([^}]*)\}/g

export interface TemplateParameter {
  key: string
  defaultValue: string
}

export function parseTemplateParameters(template: string): TemplateParameter[] {
  const params: TemplateParameter[] = []
  const seen = new Set<string>()

  let match
  const regex = new RegExp(PARAM_REGEX.source, 'g')
  while ((match = regex.exec(template)) !== null) {
    const key = match[1]
    const defaultValue = match[2]

    if (!seen.has(key)) {
      seen.add(key)
      params.push({ key, defaultValue })
    }
  }

  return params
}

export function resolveTemplate(template: string, values: Record<string, string>): string {
  return template.replace(PARAM_REGEX, (_, key: string, defaultValue: string) => {
    const value = values[key]
    return value !== undefined && value !== '' ? value : defaultValue
  })
}

export function extractDefaultValues(params: TemplateParameter[]): Record<string, string> {
  return params.reduce((acc, param) => {
    acc[param.key] = param.defaultValue
    return acc
  }, {} as Record<string, string>)
}

/**
 * resolveTemplate 로 만들어진 프롬프트에서 파라미터 값을 되찾는다 (재사용 시 파라미터 칩 복원용).
 * 왼쪽부터 한 번만 훑는다(백트래킹 없음): 파라미터 값은 다음 텍스트 조각이 처음 나오는 곳까지,
 * 마지막 텍스트 조각 앞이면 프롬프트가 그 조각으로 끝나는 곳까지다.
 * 되찾은 값으로 다시 치환한 결과가 프롬프트와 정확히 같을 때만 값을 돌려주고, 아니면
 * (직접 수정 등) 또는 파라미터가 없으면 null.
 */
export function matchTemplateParameters(
  template: string,
  prompt: string
): Record<string, string> | null {
  const tokens = parseTemplateTokens(template)
  if (!tokens.some((token) => token.type === 'parameter')) return null

  const values: Record<string, string> = {}
  let pos = 0

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    if (token.type === 'text') {
      if (!prompt.startsWith(token.content, pos)) return null
      pos += token.content.length
      continue
    }

    const next = tokens[i + 1]
    let end: number
    if (!next) {
      end = prompt.length
    } else if (next.type === 'parameter') {
      // 붙어 있는 두 파라미터는 경계를 정할 수 없다
      return null
    } else if (i + 1 === tokens.length - 1) {
      end = prompt.endsWith(next.content) ? prompt.length - next.content.length : -1
    } else {
      end = prompt.indexOf(next.content, pos)
    }
    if (end < pos) return null

    const key = token.key!
    const value = prompt.slice(pos, end)
    if (key in values && values[key] !== value) return null
    values[key] = value
    pos = end
  }

  return resolveTemplate(template, values) === prompt ? values : null
}

/**
 * 파라미터 키에 대한 현재 언어 라벨 반환 (locales/*.json 의 parameters.*)
 * 카탈로그에 없으면 원본 키 반환
 */
export function getParameterLabel(key: string): string {
  return tDynamic(`parameters.${key}`) ?? key
}

export function hasTemplateParameters(template: string): boolean {
  const regex = /\$\{([A-Z_가-힣][A-Z0-9_가-힣]*):-([^}]*)\}/
  return regex.test(template)
}

export interface TemplateToken {
  type: 'text' | 'parameter'
  content: string
  key?: string
  defaultValue?: string
}

export function parseTemplateTokens(template: string): TemplateToken[] {
  const tokens: TemplateToken[] = []
  const regex = new RegExp(PARAM_REGEX.source, 'g')

  let lastIndex = 0
  let match

  while ((match = regex.exec(template)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        content: template.slice(lastIndex, match.index),
      })
    }

    tokens.push({
      type: 'parameter',
      content: match[0],
      key: match[1],
      defaultValue: match[2],
    })

    lastIndex = regex.lastIndex
  }

  if (lastIndex < template.length) {
    tokens.push({
      type: 'text',
      content: template.slice(lastIndex),
    })
  }

  return tokens
}
